import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Plus, Edit3, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";

export default function InsightsAdmin() {
  const [articles, setArticles] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get("/admin/insights").then(({ data }) => setArticles(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/admin/insights", { title: "Untitled draft", body: [{ type: "paragraph", text: "" }] });
      navigate(`/admin/insights/${data.id}`);
    } catch { toast.error("Failed to create"); }
  };

  const publish = async (a) => {
    try { await api.post(`/admin/insights/${a.id}/publish`); toast.success("Published"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const unpublish = async (a) => {
    try { await api.post(`/admin/insights/${a.id}/unpublish`); toast.success("Unpublished"); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (a) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/admin/insights/${a.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-serif text-4xl text-deep-navy">Insights</h1><p className="text-charcoal/60">Draft, preview and publish articles.</p></div>
        <button onClick={create} data-testid="insight-create" className="inline-flex items-center gap-2 bg-deep-navy text-white px-5 py-2.5 text-sm hover:bg-restrained-blue transition-colors"><Plus size={16} /> New article</button>
      </div>

      <div className="bg-white border border-[#E5E5E5]" data-testid="insights-admin-list">
        {articles.length === 0 ? (
          <p className="p-12 text-center text-charcoal/40">No articles yet.</p>
        ) : articles.map((a) => (
          <div key={a.id} className="flex items-center justify-between p-5 border-b border-[#E5E5E5] last:border-0 hover:bg-off-white transition-colors">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 ${a.status === "published" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{a.status}</span>
                <p className="font-medium text-charcoal truncate">{a.title || "Untitled"}</p>
              </div>
              <p className="text-xs text-charcoal/40 mt-1">{a.category || "Uncategorised"} · updated {new Date(a.updated_at).toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {a.status === "published" && <a href={`/insights/${a.slug}`} target="_blank" rel="noreferrer" className="p-2 text-charcoal/50 hover:text-restrained-blue" title="View"><ExternalLink size={16} /></a>}
              <button onClick={() => navigate(`/admin/insights/${a.id}`)} data-testid={`insight-edit-${a.id}`} className="p-2 text-charcoal/50 hover:text-restrained-blue" title="Edit"><Edit3 size={16} /></button>
              {a.status === "published"
                ? <button onClick={() => unpublish(a)} className="p-2 text-charcoal/50 hover:text-amber-600" title="Unpublish"><EyeOff size={16} /></button>
                : <button onClick={() => publish(a)} data-testid={`insight-publish-${a.id}`} className="p-2 text-charcoal/50 hover:text-green-600" title="Publish"><Eye size={16} /></button>}
              <button onClick={() => remove(a)} className="p-2 text-charcoal/50 hover:text-destructive" title="Delete"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
